import { FormEvent, ReactElement, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { PaymentConfirmationDialog as ConfirmationDialog } from "./components/PaymentConfirmationDialog";

export function usePaymentSetup({
  isOpen,
  isDirty,
  isValid,
  validate,
  onClose,
  onReset,
  requiresDefaultConfirmation = false,
}: {
  isOpen: boolean;
  isDirty: boolean;
  isValid: boolean;
  validate?: () => Promise<boolean>;
  onClose: () => void;
  onReset: () => void;
  requiresDefaultConfirmation?: boolean;
}): {
  requestClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  isSubmitting: boolean;
  discardDialog: ReactElement;
} {
  const { t } = useTranslation("paymentMethods");
  const [discardOpen, setDiscardOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitting = useRef(false);
  const validating = useRef(false);
  const validated = useRef(false);
  const open = useRef(isOpen);
  open.current = isOpen;
  const [defaultConfirmationOpen, setDefaultConfirmationOpen] = useState(false);
  const defaultConfirmed = useRef(false);
  const pendingForm = useRef<HTMLFormElement | null>(null);
  const reset = useRef(onReset);
  reset.current = onReset;
  useEffect(() => {
    if (!isOpen) {
      reset.current();
      submitting.current = false;
      validating.current = false;
      validated.current = false;
      setIsSubmitting(false);
      setDiscardOpen(false);
      setDefaultConfirmationOpen(false);
      defaultConfirmed.current = false;
      pendingForm.current = null;
    }
  }, [isOpen]);
  return {
    requestClose: () => {
      if (submitting.current || validating.current) return;
      if (isDirty) setDiscardOpen(true);
      else onClose();
    },
    onSubmit: (event) => {
      const approved = validated.current;
      validated.current = false;
      if (submitting.current || validating.current) {
        event.preventDefault();
        return;
      }
      if (validate != null && !approved) {
        event.preventDefault();
        const form = event.currentTarget;
        validating.current = true;
        validate()
          .then((valid) => {
            if (!valid || !open.current || !form.isConnected) {
              validating.current = false;
              return;
            }
            // A new task lets the browser finish its first submit event and React
            // commit the encrypted hidden fields before the native provider POST.
            requestAnimationFrame(() => {
              validating.current = false;
              if (!open.current || !form.isConnected) return;
              validated.current = true;
              form.requestSubmit();
            });
          })
          .catch(() => {
            validating.current = false;
          });
        return;
      }
      if (!approved && !isValid) {
        event.preventDefault();
        return;
      }
      if (requiresDefaultConfirmation && !defaultConfirmed.current) {
        event.preventDefault();
        pendingForm.current = event.currentTarget;
        setDefaultConfirmationOpen(true);
        return;
      }
      // Only the provider's encrypted card payload belongs in the native POST.
      event.currentTarget.addEventListener(
        "formdata",
        (formEvent) => {
          if (
            "formData" in formEvent &&
            formEvent.formData instanceof FormData
          ) {
            const data = formEvent.formData;
            ["cardNumber", "expirationDate", "cvv"].forEach((name) =>
              data.delete(name)
            );
          }
        },
        { once: true }
      );
      // Keep the browser's existing native POST to the hosted payment page.
      submitting.current = true;
      setIsSubmitting(true);
    },
    isSubmitting,
    discardDialog: (
      <>
        <ConfirmationDialog
          isOpen={discardOpen}
          title={t("Discard these details?")}
          description={t("Your entered details have not been saved.")}
          cancelLabel={t("Keep editing")}
          confirmLabel={t("Discard details")}
          onClose={() => setDiscardOpen(false)}
          onCancel={() => setDiscardOpen(false)}
          onConfirm={() => {
            setDiscardOpen(false);
            reset.current();
            onClose();
          }}
        />
        <ConfirmationDialog
          isOpen={defaultConfirmationOpen}
          title={t("Use this method for Auto-Pay?")}
          description={t(
            "Future automatic payments will use this method after it has been added."
          )}
          cancelLabel={t("Keep editing")}
          confirmLabel={t("Add and use this method")}
          onClose={() => setDefaultConfirmationOpen(false)}
          onCancel={() => setDefaultConfirmationOpen(false)}
          onConfirm={() => {
            defaultConfirmed.current = true;
            setDefaultConfirmationOpen(false);
            pendingForm.current?.requestSubmit();
          }}
        />
      </>
    ),
  };
}
